import React, {useState, useEffect, useCallback} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {Box, Grid, Card, Typography} from '@mui/material';
import Banner from './components/Banner';
import CodePromptCard from './components/CodePromptCard';
import ProfileCard from './components/ProfileCard';
import SettingsCard from './components/SettingsCard';
import {getPublicCollaborateurById} from '../../../services/CollaborateurService';
import './components/styles/CollaborateurPublicView.css';

const CODE_KEY = 'collab-confirm-code';
const EXP_MS = 3 * 60 * 60 * 1000;

const saveCode = (code) => {
    localStorage.setItem(CODE_KEY, JSON.stringify({code, ts: Date.now()}));
};

const loadCode = () => {
    const raw = localStorage.getItem(CODE_KEY);
    if (!raw) return null;
    try {
        const {code, ts} = JSON.parse(raw);
        if (Date.now() - ts < EXP_MS) return code;
    } catch (_) {
    }
    localStorage.removeItem(CODE_KEY);
    return null;
};

const clearCode = () => {
    localStorage.removeItem(CODE_KEY);
};

const handleLogout = () => {
    localStorage.removeItem(CODE_KEY);
    window.location.reload();
};

const useQuery = () => new URLSearchParams(useLocation().search);

const CollaborateurPublicView = () => {
    const query = useQuery();
    const {id: idFromPath} = useParams();
    const id = idFromPath || query.get('id');
    const codeFromUrl = query.get('code') || '';

    const [code, setCode] = useState('');
    const [collab, setCollab] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const submitCode = useCallback(
        async (codeToUse = code) => {
            if (!codeToUse) return;
            try {
                setLoading(true);
                const {data} = await getPublicCollaborateurById(id, codeToUse);
                setCollab(data);
                setError(null);
                saveCode(codeToUse);
            } catch {
                setError('Code invalide ou lien expiré.');
                clearCode();
            } finally {
                setLoading(false);
            }
        },
        [code, id]
    );

    useEffect(() => {
        const cached = loadCode();
        if (cached) {
            setCode(cached);
            submitCode(cached);
            return;
        }
        if (codeFromUrl) {
            setCode(codeFromUrl);
            submitCode(codeFromUrl);
        }
    }, [id, codeFromUrl, submitCode]);

    if (!id) {
        return (
            <Card sx={{p: 3, maxWidth: 420, m: '120px auto', borderRadius: 3}}>
                <Typography color="error">Identifiant introuvable dans l’URL.</Typography>
            </Card>
        );
    }

    if (!collab) {
        return (
            <CodePromptCard
                code={code}
                setCode={setCode}
                onSubmit={() => submitCode(code)}
                loading={loading}
                error={error}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100dvh',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                bgcolor: '#fff',
            }}
        >
            <Banner/>
            <Box
                sx={{
                    position: 'relative',
                    top: {xs: '-3vh', md: '-7vh'},
                    px: {xs: 2, md: 6},
                    pb: {xs: 4, md: 8},
                }}
            >
                <Grid container spacing={4} justifyContent="center">
                    <Grid item xs={12} md={3}>
                        <ProfileCard collab={collab} onLogout={handleLogout}/>
                    </Grid>
                    <Grid item xs={12} md={7.5}>
                        <SettingsCard collab={collab}/>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default CollaborateurPublicView;
